import {
    ChangeDetectorRef,
    Directive,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    SimpleChanges,
    ViewRef
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { DfApiConfigService } from '@df/api/config/df-api-config.service';
import { App } from '@df/core/app';
import { TimeoutService } from '@df/ng/timeout.service';
import { IAddressData } from '@df/session/api/address-book/address';
import { AddressSearcher, IAddressSearcherData } from '@df/ui/form/address-searcher';
import { AddressSearcherService } from '@df/ui/form/address-searcher.service';
import { Nexus } from '@tomandco/nexus-api-types';
import find from 'lodash-es/find';
import { Subscription } from 'rxjs';

@Directive()
export abstract class AddressFieldsetComponentDef implements OnInit, OnDestroy, OnChanges, IAddressSearcherData {
    protected subscriptions = new Subscription();

    @Input({
        required: true
    })
    data!: IAddressData;

    @Input()
    form?: NgForm;

    @Output()
    searcherSelectionApplied = new EventEmitter();

    @Output()
    busy = new EventEmitter<boolean>();

    @Input()
    type?: 'billing' | 'shipping' = 'shipping';

    protected allCountries?: Nexus.Lib.General.Country[];
    protected shippingCountries?: Nexus.Lib.General.Country[];

    /**
     * If engine exists we will try to use address search here
     * @type {AddressSearcher}
     */
    searcher?: AddressSearcher;

    isFormShown = false;

    constructor(
        @Optional() protected addressSearcher: AddressSearcherService,
        readonly app: App,
        protected changeDetectorRef: ChangeDetectorRef,
        protected timeoutService: TimeoutService,
        protected dfApiConfigService: DfApiConfigService
    ) {
        this.syncCountries();

        // clear query after customer status change
        this.subscriptions.add(
            app.customer.model.obs.status.subscribe(() => {
                if (this.searcher) {
                    this.searcher.query = undefined;
                }
            })
        );
    }

    ngOnInit(): void {
        if (this.addressSearcher) {
            this.searcher = this.getAddressSearcher();
            this.subscriptions.add(this.searcher.selectionApplied.subscribe(() => this.onSearcherSelectionApplied()));
            this.subscriptions.add(
                this.searcher.status.subscribe(() => {
                    this.busy.emit(this.searcher?.status.busy);
                })
            );
        }
        if (this.data?.street_1 && this.searcher) {
            this.searcher.isManual = true;
        }
        this.checkManualToggleNeeded();
    }

    ngOnChanges(changesObj: SimpleChanges) {
        if (changesObj['data'] && this.data) {
            this.data.country_id = this.data.country_id || this.app.data.defaultCountry;
        }
    }

    ngOnDestroy(): void {
        if (this.searcher) {
            this.searcher.onDestroy();
        }
        this.subscriptions.unsubscribe();
    }

    protected getAddressSearcher(): AddressSearcher {
        return this.addressSearcher.create(this);
    }

    /**
     * Initialize 2 lists of countries - complete and shipping only
     */
    protected syncCountries() {
        this.allCountries = [...(this.dfApiConfigService.countries || [])]
            .filter(i => typeof i.name === 'string' && i.name)
            .sort((a, b) => a.name.localeCompare(b.name));
        this.shippingCountries = this.allCountries.filter(country => !country.noShipping);
    }

    /**
     * Countries list
     * @type {array}
     */
    get countries(): Nexus.Lib.General.Country[] {
        return (!this.type || this.type === 'billing' ? this.allCountries : this.shippingCountries) || [];
    }

    /**
     * Regions list
     * @see onCountryChange
     * @type {array}
     */
    get regions(): Nexus.Lib.General.Region[] | undefined {
        const countryModel: Nexus.Lib.General.Country | undefined = this.country;

        if (countryModel && countryModel.regions && countryModel.regions.length) {
            return countryModel.regions;
        }

        return undefined;
    }

    /**
     * Returns currently selected country code
     * @return {string | undefined}
     */
    get countryCode(): string | undefined {
        return this.data ? this.data.country_id : undefined;
    }

    /**
     * Returns currently selected country model
     * @return {Nexus.Lib.General.Country | undefined}
     */
    get country(): Nexus.Lib.General.Country | undefined {
        return find(this.countries, { code: this.countryCode });
    }

    get regionRequired(): boolean {
        return !!(this.country && this.country.region_required);
    }

    get postcodeRequired(): boolean {
        return !!(this.country && !this.country.postcode_optional);
    }

    /**
     * Triggered when country changes
     */
    onCountryChange(): void {
        if (this.regions) {
            if (this.data) {
                this.data.region = undefined;
            }
            if (this.data.region_id && !this.regions.find(i => i.id === this.data.region_id)) {
                this.data.region_id = undefined;
            }
        } else if (this.data) {
            this.data.region_id = undefined;
        }
        this.detectChanges();
    }

    /**
     * If 'enter manually' checkbox switch is needed
     * it is only needed till the first time form appears, if things chnage isFormShown property is changed here
     */
    checkManualToggleNeeded(): boolean {
        if (!this.data || !this.searcher) {
            return false;
        }
        const isForced = this.searcher?.isManual || !this.searcher?.isAvailable || this.form?.submitAttempt || this.data.street_1;
        if (!this.isFormShown && isForced) {
            this.isFormShown = true;
        }
        return !isForced;
    }

    displayForm() {
        this.isFormShown = true;
        if (this.searcher) {
            this.searcher.isManual = true;
        }
        this.detectChanges();
    }
    /**
     * Flag if form should be visible
     * @type {boolean}
     *
     * @depreciated Use ngIf="checkManualToggle()" on manual switch, and isFormShown variable
     */
    get isFormVisible(): boolean {
        return !!(
            !this.searcher ||
            this.searcher.isManual ||
            !this.searcher.isAvailable ||
            !this.data ||
            !!this.data.street_1 ||
            this.form?.submitAttempt
        );
    }

    onSearcherSelectionApplied(): void {
        this.searcherSelectionApplied.emit();
        this.timeoutService.setTimeout(() => {
            this.detectChanges();
        }, 0);
    }

    detectChanges(): void {
        if (!(<ViewRef>this.changeDetectorRef).destroyed) {
            this.changeDetectorRef.detectChanges();
        }
    }
}
